import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";

export const BackfillSyncTypeSchema = z.enum([
  "initial_backfill",
  "manual",
  "scheduled",
]);

export const BackfillSyncSeveritySchema = z.enum(["medium", "high"]);
export type BackfillSyncSeverity = z.infer<typeof BackfillSyncSeveritySchema>;

/** All message channels that can appear in backfill records. */
export const BackfillMessageChannelSchema = z.enum([
  "gmail",
  "outlook",
  "customEmail",
  "redoChat",
  "facebookDm",
  "facebookComments",
  "instagramDm",
  "instagramComments",
  "sms",
  "voice",
  "attentive",
  "postscript",
  "tiktok",
  "contactForm",
  "internalNote",
  "mobileMessage",
]);

/** All channels the admin manual backfill can dispatch to. */
export const BackfillManualChannelSchema = z.enum([
  "gmail",
  "outlook",
  "facebookDm",
  "facebookComments",
  "instagramDm",
  "instagramComments",
]);
export type BackfillManualChannel = z.infer<typeof BackfillManualChannelSchema>;

const breakdownBucketSchema = <T extends z.ZodTypeAny>(keySchema: T) =>
  z.object({ key: keySchema, count: z.number().int().min(0) });

const backfillMessageSchema = z.object({
  id: z.string(),
  syncId: z.string(),
  messageId: z.string(),
  accountId: z.string(),
  teamId: z.string(),
  integrationId: z.string(),
  integrationType: z.string(),
  syncType: BackfillSyncTypeSchema,
  severity: BackfillSyncSeveritySchema.nullable(),
  channel: BackfillMessageChannelSchema,
  externalMessageId: z.string(),
  messageContent: z.string().nullable(),
  messageSentAt: z.string(),
  linkedAt: z.string(),
  syncEarlyDate: z.string(),
  syncLateDate: z.string(),
});

export const getSupportBackfillMessages = unaryRpc({
  name: "getSupportBackfillMessages" as const,
  input: z.object({
    startDate: z.date().optional(),
    endDate: z.date().optional(),
    teamId: z.string().optional(),
    teamIds: z.array(z.string()).optional(),
    syncTypes: z.array(BackfillSyncTypeSchema).optional(),
    severities: z.array(BackfillSyncSeveritySchema).optional(),
    channels: z.array(BackfillMessageChannelSchema).optional(),
    limit: z.number().int().min(1).max(200).optional(),
  }),
  output: z.object({
    totalCount: z.number().int().min(0),
    daily: z.array(
      z.object({
        date: z.string(),
        count: z.number().int().min(0),
        bySyncType: z.record(z.string(), z.number().int().min(0)),
        byChannel: z.record(z.string(), z.number().int().min(0)),
      }),
    ),
    bySyncType: z.array(breakdownBucketSchema(BackfillSyncTypeSchema)),
    byChannel: z.array(breakdownBucketSchema(BackfillMessageChannelSchema)),
    recentMessages: z.array(backfillMessageSchema),
  }),
});

export type GetSupportBackfillMessagesInput = z.infer<
  typeof getSupportBackfillMessages.input
>;

export type GetSupportBackfillMessagesOutput = z.infer<
  typeof getSupportBackfillMessages.output
>;
